import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FiPlus, FiSearch, FiEdit, FiTrash2, FiEye, FiRefreshCw, FiDownload } from 'react-icons/fi';

const EmployeeList = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 10 };
      if (search) params.search = search;
      if (department) params.department = department;
      if (status) params.status = status;
      const res = await api.get('/employees', { params });
      setEmployees(res.data.employees);
      setTotalPages(res.data.pages);
    } catch (err) {
      toast.error('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  }, [page, search, department, status]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    api.get('/departments').then(res => setDepartments(res.data)).catch(() => {});
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success('Employee deleted');
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleDownload = async () => {
    try {
      const res = await api.get('/employees/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employees.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Download failed');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Employees</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn-secondary" onClick={fetchEmployees} title="Refresh"><FiRefreshCw /></button>
          {user.role === 'admin' && (
            <button className="btn-secondary" onClick={handleDownload} title="Download CSV"><FiDownload /> Download</button>
          )}
          {(user.role === 'admin' || user.role === 'hr') && (
            <Link to="/employees/add" className="btn-primary"><FiPlus /> Add Employee</Link>
          )}
        </div>
      </div>

      <div className="filters-row">
        <div className="search-box">
          <FiSearch />
          <input placeholder="Search employees..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select value={department} onChange={(e) => { setDepartment(e.target.value); setPage(1); }}>
          <option value="">All Departments</option>
          {departments.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
        </select>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      {loading ? <div className="loading">Loading...</div> : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Sr. No.</th><th>Employee ID</th><th>Name</th><th>Email</th><th>Department</th><th>Designation</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp, index) => (
                <tr key={emp._id}>
                  <td>{(page - 1) * 10 + index + 1}</td>
                  <td>{emp.employeeId}</td>
                  <td>{emp.name}</td>
                  <td>{emp.email}</td>
                  <td>{emp.department?.name || 'N/A'}</td>
                  <td>{emp.designation}</td>
                  <td><span className={`status-badge status-${emp.status.toLowerCase()}`}>{emp.status}</span></td>
                  <td className="actions">
                    <Link to={`/employees/${emp._id}`} className="btn-sm btn-info"><FiEye /></Link>
                    {(user.role === 'admin' || user.role === 'hr') && (
                      <Link to={`/employees/${emp._id}/edit`} className="btn-sm btn-warning"><FiEdit /></Link>
                    )}
                    {user.role === 'admin' && (
                      <button className="btn-sm btn-danger" onClick={() => handleDelete(emp._id)}><FiTrash2 /></button>
                    )}
                  </td>
                </tr>
              ))}
              {employees.length === 0 && <tr><td colSpan="8" style={{ textAlign: 'center' }}>No employees found</td></tr>}
            </tbody>
          </table>

          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
            <span>Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        </>
      )}
    </div>
  );
};

export default EmployeeList;
